import React, { useState, useEffect } from 'react';
import { X, Settings, CheckCircle } from 'lucide-react';
import '../css/cookie-banner.css';

const CookieBanner = () => {
    const [showBanner, setShowBanner] = useState(false);
    const [showPreferences, setShowPreferences] = useState(false);
    const [cookiePreferences, setCookiePreferences] = useState({
        essential: true, // Always true and cannot be changed
        analytics: true, // Google Analytics
        preferences: true // Preference cookies like language, etc.
    });

    // Check if user has already made cookie choices
    useEffect(() => {
        const consent = localStorage.getItem('cookie-consent');
        // If no consent has been given, show the banner
        if (!consent) {
            setShowBanner(true);
        } else {
            // Parse stored preferences
            try {
                const savedPreferences = JSON.parse(consent);
                setCookiePreferences(savedPreferences);
                
                // Initialize Google Analytics if analytics consent was given
                if (savedPreferences.analytics) {
                    if (!window.gtag) {
                        initializeGoogleAnalytics();
                    }
                    // initializeGoogleAnalytics();
                }
            } catch (error) {
                console.error('Error parsing cookie consent:', error);
                setShowBanner(true);
            }
        }
    }, []);

    // Initialize Google Analytics
    const initializeGoogleAnalytics = () => {
        const script1 = document.createElement('script');
        script1.async = true;
        script1.src = 'https://www.googletagmanager.com/gtag/js?id=G-JZD8G82Y4K';
    
        script1.onload = () => {
            const script2 = document.createElement('script');
            script2.innerHTML = `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', 'G-JZD8G82Y4K');
                gtag('event', 'page_view');
            `;
            document.head.appendChild(script2);
        };
    
        document.head.appendChild(script1);
    };
    
    // Accept all cookies
    const acceptAllCookies = () => {
        const allAccepted = {
            essential: true,
            analytics: true,
            preferences: true
        };
    
        setCookiePreferences(allAccepted);
        localStorage.setItem('cookie-consent', JSON.stringify(allAccepted));
        setShowBanner(false);
        setShowPreferences(false);
    
        if (!window.gtag) {
            initializeGoogleAnalytics();
        }
    };
    

    // Accept only essential cookies
    const acceptEssentialOnly = () => {
        const essentialOnly = {
            essential: true,
            analytics: false,
            preferences: false
        };
        
        setCookiePreferences(essentialOnly);
        localStorage.setItem('cookie-consent', JSON.stringify(essentialOnly));
        setShowBanner(false);
        setShowPreferences(false);
    };

    // Save custom preferences
    const savePreferences = () => {
        localStorage.setItem('cookie-consent', JSON.stringify(cookiePreferences));
        setShowBanner(false);
        setShowPreferences(false);
    
        if (cookiePreferences.analytics && !window.gtag) {
            initializeGoogleAnalytics();
        }
    };    

    // Show/hide cookie preference details
    const togglePreferences = () => {
        setShowPreferences(!showPreferences);
    };

    // Handle change in checkbox
    const handlePreferenceChange = (type) => {
        setCookiePreferences(prev => ({
            ...prev,
            [type]: !prev[type]
        }));
    };

    // Function to reopen the cookie banner manually
    const openCookieSettings = () => {
        setShowBanner(true);
        setShowPreferences(true);
    };

    // Make this function accessible globally
    window.openCookieSettings = openCookieSettings;

    if (!showBanner) return null;

    return (
        <div className="cookie-banner-container">
            <div className={`cookie-banner ${showPreferences ? 'preferences-open' : ''}`}>
                <div className="cookie-banner-header">
                    <h2>Préférences cookies</h2>
                    <button 
                        onClick={() => setShowBanner(false)} 
                        className="cookie-close-btn"
                        aria-label="Fermer"
                    >
                        <X size={24} />
                    </button>
                </div>
                
                <div className="cookie-banner-content">
                    <p>
                        Nous utilisons des cookies pour améliorer votre expérience de navigation,
                        analyser le trafic du site et à des fins de marketing. Vous pouvez gérer vos préférences ci-dessous.
                    </p>
                    
                    {showPreferences && (
                        <div className="cookie-preferences">
                            <div className="cookie-preference-option">
                                <div className="preference-header">
                                    <label className="disabled">
                                        <input 
                                            type="checkbox" 
                                            checked={cookiePreferences.essential} 
                                            disabled
                                        />
                                        <span className="checkbox-label">Cookies essentiels</span>
                                    </label>
                                    <div className="badge-essential">Toujours actif</div>
                                </div>
                                <p className="preference-description text-muted small">
                                    Nécessaires au fonctionnement du site.
                                </p>
                            </div>
                            
                            <div className="cookie-preference-option">
                                <div className="preference-header">
                                    <label>
                                        <input 
                                            type="checkbox" 
                                            checked={cookiePreferences.analytics} 
                                            onChange={() => handlePreferenceChange('analytics')}
                                        />
                                        <span className="checkbox-label">Cookies d'analyse</span>
                                    </label>
                                </div>
                                <p className="preference-description text-muted small">
                                    Pour mesurer l'audience et analyser la navigation.
                                </p>
                            </div>
                            
                            <div className="cookie-preference-option">
                                <div className="preference-header">
                                    <label>
                                        <input 
                                            type="checkbox" 
                                            checked={cookiePreferences.preferences} 
                                            onChange={() => handlePreferenceChange('preferences')}
                                        />
                                        <span className="checkbox-label">Cookies de préférences</span>
                                    </label>
                                </div>
                                <p className="preference-description text-muted small">
                                    Pour enregistrer vos préférences de navigation.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
                
                <div className="cookie-banner-actions">
                    {!showPreferences ? (
                        <>
                            <button onClick={togglePreferences} className="btn btn-outline-dark">
                                {/* <Settings size={16} className="me-2" /> */}
                                Personnaliser
                            </button>
                            <button onClick={acceptEssentialOnly} className="btn btn-outline-secondary">
                                Refuser tout
                            </button>
                            <button onClick={acceptAllCookies} className="btn btn-warning">
                                {/* <CheckCircle size={16} className="me-2" /> */}
                                Tout accepter
                            </button>
                        </>
                    ) : (
                        <>
                            <button onClick={() => setShowPreferences(false)} className="btn btn-outline-dark">
                                Retour
                            </button>
                            <button onClick={savePreferences} className="btn btn-warning">
                                Enregistrer mes préférences
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CookieBanner;